<template>
  <v-container>
    <!-- Loading State -->
    <div class="d-flex justify-center align-center py-12" v-if="loadingInfo">
      <div class="text-center">
        <v-progress-circular indeterminate color="primary" size="64" width="6" class="mb-4" />
        <div class="text-h6 grey--text">Cargando ficha de producto...</div>
      </div>
    </div>

    <div v-else>
      <ValidationObserver v-slot="{ handleSubmit }">
        <v-form @submit.prevent="handleSubmit(save)">

          <!-- Relbase Sync Info Banner (Opción A: Runflow manda) -->
          <v-alert
            v-if="relbaseEnabled && id > 0 && relbase_product_id"
            type="info"
            dense
            outlined
            class="mb-4"
            border="left"
            colored-border
            color="#35BDC8"
            icon="mdi-sync"
          >
            Producto sincronizado con Relbase. Los cambios se sincronizarán automáticamente al guardar.
          </v-alert>

          <!-- Header Actions -->
          <v-row class="mb-6 align-center px-4">
            <div class="d-flex align-center py-4">
              <v-btn icon color="primary" @click="$router.push('/products')" class="mr-4 grey lighten-4 shadow-sm">
                <v-icon small>fas fa-chevron-left</v-icon>
              </v-btn>
              <div>
                <div class="text-h4 font-weight-black primary--text d-flex align-center">
                  {{ id > 0 ? 'Editar Producto' : 'Nuevo Producto' }}
                </div>
                <div class="caption grey--text font-weight-medium">Configuración de especificaciones y costos</div>
              </div>
            </div>
            <v-spacer></v-spacer>
            <div class="d-flex align-center">
              <v-switch
                v-model="status"
                :label="status ? 'Producto Activo' : 'Producto Inactivo'"
                color="success"
                inset
                dense
                hide-details
                class="mr-8 mt-0 font-weight-bold"
              ></v-switch>
              <v-chip
                v-if="relbaseEnabled && id > 0 && relbase_product_id"
                color="#35BDC8"
                dark
                small
                label
                class="mr-2 font-weight-bold"
              >
                <v-icon small left>mdi-check-circle</v-icon>
                Sincronizado con Relbase
              </v-chip>
              <v-btn
                v-if="relbaseEnabled && id > 0 && relbase_product_id"
                outlined x-small color="grey" class="mr-4"
                :loading="syncLoading"
                @click="syncWithRelbase(true)"
                title="Re-sincronizar con Relbase"
              >
                <v-icon x-small>mdi-sync</v-icon>
              </v-btn>
              <v-btn
                v-if="relbaseEnabled && id > 0 && !relbase_product_id"
                outlined
                small
                color="#35BDC8"
                class="mr-4"
                :loading="syncLoading"
                @click="syncWithRelbase"
              >
                <v-icon small left>mdi-sync</v-icon>
                Sincronizar con Relbase
              </v-btn>
              <v-btn
                :loading="isLoading"
                type="submit"
                color="primary"
                depressed
                class="px-8 shadow-sm"
              >
                <v-icon left small>fas fa-save</v-icon>
                Guardar Cambios
              </v-btn>
            </div>
          </v-row>

          <v-row>
            <!-- Left Column: General Info & Specifications -->
            <v-col cols="12" lg="8">
              <v-card outlined class="modern-card mb-6">
                <v-sheet color="grey lighten-4" class="px-6 py-3 border-bottom d-flex align-center">
                  <v-icon small color="primary" class="mr-3">fas fa-info-circle</v-icon>
                  <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2 text-uppercase">Información General</span>
                </v-sheet>
                
                <v-card-text class="pa-6">
                  <v-row>
                    <v-col cols="12" md="6">
                      <ValidationProvider name="Código" rules="required" v-slot="{ errors }">
                        <v-text-field
                          v-model="sku"
                          label="Código (SKU)"
                          placeholder="Ej: PRD-001"
                          outlined
                          dense
                          :error-messages="errors"
                          :disabled="productFiscalLocked"
                          :hint="productFiscalLocked ? 'Sincronizado con Relbase — editar desde Relbase y Re-sincronizar' : ''"
                          persistent-hint
                        ></v-text-field>
                      </ValidationProvider>
                    </v-col>
                    <v-col cols="12" md="6">
                      <ValidationProvider name="Nombre" rules="required" v-slot="{ errors }">
                        <v-text-field
                          v-model="name"
                          label="Nombre del Producto"
                          placeholder="Descripción comercial"
                          outlined
                          dense
                          :error-messages="errors"
                          :disabled="productFiscalLocked"
                        ></v-text-field>
                      </ValidationProvider>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="family"
                        :items="families"
                        item-text="name"
                        item-value="id"
                        label="Familia"
                        outlined
                        dense
                        clearable
                        placeholder="Sin familia"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="line"
                        :items="lines"
                        item-text="name"
                        item-value="id"
                        label="Línea"
                        outlined
                        dense
                        clearable
                        placeholder="Sin línea"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <customer-complete2 :dense="true" v-model="customer" :customer_name="customer_name" />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Production Logic Sections (Processes & Materials) -->
              <v-card outlined class="modern-card mb-6">
                <v-sheet color="grey lighten-4" class="px-6 py-3 border-bottom d-flex align-center">
                  <v-icon small color="primary" class="mr-3">fas fa-cogs</v-icon>
                  <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2 text-uppercase">Procesos de Producción</span>
                  <v-spacer></v-spacer>
                  <v-btn x-small color="primary" depressed @click="addingProcess = true">
                    <v-icon x-small left>fas fa-plus</v-icon> Añadir Proceso
                  </v-btn>
                </v-sheet>
                
                <v-card-text class="pa-0">
                  <v-simple-table class="modern-table no-border-radius" v-if="product_receta.length > 0">
                    <thead>
                      <tr>
                        <th class="text-center" width="50">#</th>
                        <th class="text-left">Operación / Proceso</th>
                        <th class="text-right" width="120">Costo Unit.</th>
                        <th class="text-center" width="100">Cant. PP</th>
                        <th class="text-center" width="100">Unidad</th>
                        <th class="text-center" width="60"></th>
                      </tr>
                    </thead>
                    <draggable v-model="product_receta" draggable=".recipe-row" tag="tbody">
                      <tr v-for="(item, index) in product_receta" :key="'proc-'+index" class="recipe-row">
                        <td class="text-center grey--text caption font-weight-bold">{{ index + 1 }}</td>
                        <td>
                          <div class="body-2 font-weight-medium primary--text">{{ item.name }}</div>
                          <div class="caption grey--text">ID: {{ item.id }}</div>
                        </td>
                        <td class="text-right font-weight-bold grey--text text--darken-3">{{ item.price | currency('$',0) }}</td>
                        <td>
                          <v-text-field 
                            v-model="item.qty" 
                            type="number" 
                            dense 
                            outlined 
                            hide-details 
                            class="modern-input-small"
                          />
                        </td>
                        <td class="text-center">
                          <v-chip x-small label outlined color="grey lighten-1">{{ item.unit_name }}</v-chip>
                        </td>
                        <td class="text-center">
                          <v-btn icon x-small color="error" @click="deleteProcess(item)">
                            <v-icon x-small>fas fa-trash-alt</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </draggable>
                  </v-simple-table>
                  <div v-else class="pa-12 text-center grey--text">
                    <v-icon x-large class="mb-2 opacity-20">fas fa-stream</v-icon>
                    <div class="caption">No hay procesos definidos para este producto.</div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card outlined class="modern-card mb-6">
                <v-sheet color="grey lighten-4" class="px-6 py-3 border-bottom d-flex align-center">
                  <v-icon small color="primary" class="mr-3">fas fa-cubes</v-icon>
                  <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2 text-uppercase">Materiales e Insumos</span>
                  <v-spacer></v-spacer>
                  <v-btn x-small color="primary" depressed @click="addingSupply = true">
                    <v-icon x-small left>fas fa-plus</v-icon> Añadir Material
                  </v-btn>
                </v-sheet>
                
                <v-card-text class="pa-0">
                  <v-simple-table class="modern-table no-border-radius" v-if="supplies.length > 0">
                    <thead>
                      <tr>
                        <th class="text-center" width="50">#</th>
                        <th class="text-left">Insumo / Material</th>
                        <th class="text-right" width="120">Costo Unit.</th>
                        <th class="text-center" width="100">Cantidad</th>
                        <th class="text-center" width="100">Unidad</th>
                        <th class="text-center" width="60"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in supplies" :key="'supp-'+index">
                        <td class="text-center grey--text caption font-weight-bold">{{ index + 1 }}</td>
                        <td>
                          <div class="d-flex align-center">
                            <span class="body-2 font-weight-medium primary--text">{{ item.name }}</span>
                            <v-icon v-if="item.has_dimension" x-small class="ml-2 grey--text">fas fa-ruler-combined</v-icon>
                          </div>
                          <div v-if="item.has_dimension" class="caption info--text font-weight-bold">
                            Dim: {{ item.width }} x {{ item.height }} cm
                          </div>
                        </td>
                        <td class="text-right font-weight-bold grey--text text--darken-3">{{ item.price | currency('$',0) }}</td>
                        <td>
                          <v-text-field 
                            v-model="item.qty" 
                            type="number" 
                            dense 
                            outlined 
                            hide-details 
                            class="modern-input-small"
                          />
                        </td>
                        <td class="text-center">
                          <v-chip x-small label outlined color="grey lighten-1">{{ item.unit_name }}</v-chip>
                        </td>
                        <td class="text-center">
                          <v-btn icon x-small color="error" @click="deleteSupply(item)">
                            <v-icon x-small>fas fa-trash-alt</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                  <div v-else class="pa-12 text-center grey--text">
                    <v-icon x-large class="mb-2 opacity-20">fas fa-box-open</v-icon>
                    <div class="caption">No hay materiales asignados.</div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Right Column: Specs & Dimensions -->
            <v-col cols="12" lg="4">
              <v-card outlined class="modern-card mb-6 highlight-card">
                <v-sheet color="primary darken-1" dark class="px-6 py-3 d-flex align-center">
                  <v-icon small class="mr-3">fas fa-expand-arrows-alt</v-icon>
                  <span class="text-subtitle-2 font-weight-bold">Dimensiones Base</span>
                </v-sheet>
                
                <v-card-text class="pa-6">
                  <v-row dense>
                    <v-col cols="6">
                      <ValidationProvider name="Ancho" rules="required" v-slot="{ errors }">
                        <v-text-field
                          v-model="width"
                          label="Ancho (cm)"
                          type="number"
                          outlined
                          dense
                          suffix="cm"
                          :error-messages="errors"
                        ></v-text-field>
                      </ValidationProvider>
                    </v-col>
                    <v-col cols="6">
                      <ValidationProvider name="Alto" rules="required" v-slot="{ errors }">
                        <v-text-field
                          v-model="height"
                          label="Alto (cm)"
                          type="number"
                          outlined
                          dense
                          suffix="cm"
                          :error-messages="errors"
                        ></v-text-field>
                      </ValidationProvider>
                    </v-col>
                  </v-row>
                  
                  <div class="bg-primary-light pa-4 rounded-lg border-primary-light mt-2">
                    <div class="d-flex justify-space-between align-center">
                      <div class="d-flex align-center">
                        <v-icon x-small color="primary" class="mr-2">fas fa-vector-square</v-icon>
                        <span class="caption font-weight-bold primary--text text-uppercase">Área Calculada</span>
                      </div>
                      <div class="text-h6 font-weight-black primary--text">
                        {{ (parseFloat(width || 0) * parseFloat(height || 0) * 0.0001) | number('0.000') }} m²
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <v-card outlined class="modern-card mb-6">
                <v-sheet color="grey lighten-4" class="px-6 py-3 border-bottom d-flex align-center">
                  <v-icon small color="primary" class="mr-3">fas fa-money-bill-wave</v-icon>
                  <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2 text-uppercase">Configuración de Venta</span>
                </v-sheet>
                
                <v-card-text class="pa-6">
                  <ValidationProvider name="Precio" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="price"
                      label="Precio de Venta"
                      type="number"
                      outlined
                      dense
                      prefix="$"
                      :disabled="is_margin"
                      :error-messages="errors"
                      class="price-field"
                    >
                      <template v-slot:append>
                        <v-btn icon x-small @click="sell_unit_m2 = !sell_unit_m2" :color="sell_unit_m2 ? 'primary' : 'grey'">
                          <v-icon x-small>{{ sell_unit_m2 ? 'fas fa-th' : 'fas fa-cube' }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </ValidationProvider>

                  <div class="d-flex align-center justify-space-between mt-n2 mb-4">
                    <span class="caption grey--text font-weight-medium">Unidad de venta: {{ sell_unit_m2 ? 'Metros Cuadrados' : 'Unidad' }}</span>
                  </div>

                  <v-divider class="mb-4"></v-divider>

                  <div class="bg-grey-light pa-4 rounded-lg">
                    <v-checkbox
                      v-model="is_margin"
                      label="Calcular por Margen"
                      hide-details
                      dense
                      color="primary"
                      class="mt-0 font-weight-bold mb-2"
                      @change="is_margin ? price = 0 : null"
                    ></v-checkbox>
                    
                    <v-expand-transition>
                      <v-text-field
                        v-show="is_margin"
                        v-model="margin_price"
                        label="Margen Deseado (%)"
                        type="number"
                        outlined
                        dense
                        suffix="%"
                        hide-details
                        class="mt-2"
                      ></v-text-field>
                    </v-expand-transition>
                  </div>
                </v-card-text>
              </v-card>

              <v-card outlined class="modern-card">
                <v-card-text class="pa-4 d-flex align-center bg-grey-light">
                  <v-switch
                    v-model="logistic"
                    :label="logistic ? 'Modo Logístico' : 'Modo Producción'"
                    :color="logistic ? 'primary' : 'grey darken-1'"
                    inset
                    dense
                    hide-details
                    class="mt-0 font-weight-medium"
                  ></v-switch>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon x-small class="ml-2 grey--text" v-bind="attrs" v-on="on">fas fa-question-circle</v-icon>
                    </template>
                    <span>Define si el producto se gestiona como stock o bajo fabricación directa</span>
                  </v-tooltip>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </ValidationObserver>
    </div>

    <!-- Modals -->
    <v-dialog v-model="addingProcess" max-width="800" persistent>
      <v-card outlined class="modern-card overflow-hidden">
        <v-sheet color="grey lighten-4" class="px-6 py-4 border-bottom d-flex align-center">
          <v-icon color="primary" class="mr-3">fas fa-cogs</v-icon>
          <div class="text-h5 font-weight-bold">Agregar Proceso Producivo</div>
          <v-spacer></v-spacer>
          <v-btn icon small @click="addingProcess = false"><v-icon small>fas fa-times</v-icon></v-btn>
        </v-sheet>
        <v-card-text class="pa-6">
          <add-cost @add="addProcess" :is_supply="false" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="addingSupply" max-width="800" persistent>
      <v-card outlined class="modern-card overflow-hidden">
        <v-sheet color="grey lighten-4" class="px-6 py-4 border-bottom d-flex align-center">
          <v-icon color="primary" class="mr-3">fas fa-cubes</v-icon>
          <div class="text-h5 font-weight-bold">Agregar Material / Insumo</div>
          <v-spacer></v-spacer>
          <v-btn icon small @click="addingSupply = false"><v-icon small>fas fa-times</v-icon></v-btn>
        </v-sheet>
        <v-card-text class="pa-6">
          <add-cost @add="addSupply" :is_supply="true" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { getProduct, createProduct, updateProduct, fetchFamily, fetchLines } from '@/api/products'
import { getCustomer } from '@/api/customers'
import { syncProductRelbase } from '@/api/relbase'
import notifications from '@/utils/notifications'
import AddCost from '@/components/products/costs/AddCost'
import CustomerComplete2 from '@/components/customers/CustomerComplete2'
import draggable from 'vuedraggable'
import NumberLabel from '@/components/utils/NumberLabel'
import { calcSheets } from '@/utils/productCost'

export default {
  name: 'ProductCreateUpdate',
  components: { CustomerComplete2, AddCost, draggable, NumberLabel },
  data: () => ({
    id: -1,
    sku: '',
    name: '',
    width: 0,
    height: 0,
    price: 0,
    status: true,
    sell_unit_m2: true,
    customer: null,
    customer_name: '',
    family: '',
    line: '',
    logistic: false,
    is_margin: false,
    margin_price: 0,
    families: [],
    lines: [],
    product_receta: [],
    supplies: [],
    addingProcess: false,
    addingSupply: false,
    isLoading: false,
    loadingInfo: false,
    syncLoading: false,
    relbase_product_id: null,
    hasUnsavedChanges: false,
  }),
  async mounted() {
    this.families = this.$store.getters.getFamilies
    this.lines = this.$store.getters.getLines

    if (this.$route.params.product) {
      const product_id = this.$route.params.product
      this.loadingInfo = true
      try {
        const data = await getProduct(product_id)
        this.id = data.id
        this.sku = data.sku
        this.name = data.name
        this.width = data.width
        this.height = data.height
        this.price = data.price
        this.status = data.status
        this.sell_unit_m2 = data.sell_unit_m2
        this.customer = data.customer
        this.customer_name = data.customer_name
        this.family = data.family
        this.line = data.line
        this.product_receta = data.recipe || []
        this.supplies = data.product_supplies || []
        this.logistic = data.logistic
        this.is_margin = data.is_margin
        this.margin_price = data.margin_price
        this.relbase_product_id = data.relbase_product_id || null

        // Marcar como sin cambios después de cargar los datos originales
        this.$nextTick(() => { this.hasUnsavedChanges = false })

        if (this.customer) {
          const customerData = await getCustomer(this.customer)
          this.customer = customerData
          this.customer_name = customerData.name
        }
      } catch (err) {
        notifications.showError('Error cargando producto: ' + err.message)
      } finally {
        this.loadingInfo = false
      }
    }
    
    if (this.$route.params.duplicate) {
      this.id = -1
      this.name = "Copia de " + this.name
      this.sku = ''
    }
  },
  computed: {
    relbaseEnabled() {
      return this.$store.getters.getCompany.relbase_integration_enabled
    },
    // Opción A: Runflow manda — nunca bloqueamos campos por sync con Relbase
    productFiscalLocked() {
      return false
    },
  },
  watch: {
    width() { this.updateSupplySheetQty(); this.hasUnsavedChanges = true },
    height() { this.updateSupplySheetQty(); this.hasUnsavedChanges = true },
    name() { this.hasUnsavedChanges = true },
    sku() { this.hasUnsavedChanges = true },
    price() { this.hasUnsavedChanges = true },
    family() { this.hasUnsavedChanges = true },
    status() { this.hasUnsavedChanges = true },
  },
  methods: {
    updateSupplySheetQty() {
      this.supplies.filter(s => s.has_dimension).forEach(s => {
        s.qty = calcSheets({ width: this.width, height: this.height }, s)
      })
    },
    addProcess(item) {
      this.product_receta.push({ ...item, qty: 1 })
      this.addingProcess = false
    },
    deleteProcess(item) {
      const index = this.product_receta.indexOf(item)
      this.product_receta.splice(index, 1)
    },
    addSupply(item) {
      const new_data = { ...item, qty: item.has_dimension ? calcSheets({ width: this.width, height: this.height }, item) : 1 }
      this.supplies.push(new_data)
      this.addingSupply = false
    },
    deleteSupply(item) {
      const index = this.supplies.indexOf(item)
      this.supplies.splice(index, 1)
    },
    async syncWithRelbase(force = false) {
      // Opción A: Runflow manda. Primero guardar si hay cambios pendientes,
      // para que Relbase reciba los datos actuales del formulario (no datos stale de DB).
      if (this.hasUnsavedChanges) {
        notifications.showError(
          'Guarda los cambios primero',
          'Hay cambios sin guardar. Guarda el producto para sincronizar los datos actuales con Relbase.'
        )
        return
      }
      this.syncLoading = true
      try {
        const result = await syncProductRelbase(this.id, force)
        if (result.status === 'created' || result.status === 'updated') {
          this.relbase_product_id = result.relbase_product_id
          const action = result.status === 'created' ? 'creado' : 'actualizado'
          notifications.showSuccess('Sincronizado con Relbase', `Producto ${action} en Relbase`)
        } else if (result.status === 'error') {
          notifications.showError('Error al sincronizar con Relbase', result.message || '')
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || String(e)
        notifications.showError('Error al sincronizar con Relbase', msg)
      } finally {
        this.syncLoading = false
      }
    },
    _showSyncNotification(response, successTitle) {
      const sync = response && response.sync_result
      if (sync && sync.status === 'created') {
        notifications.showSuccess(successTitle, 'Sincronizado con Relbase (creado)')
      } else if (sync && sync.status === 'updated') {
        notifications.showSuccess(successTitle, 'Sincronizado con Relbase (actualizado)')
      } else if (sync && sync.status === 'error') {
        notifications.showSuccess(successTitle, 'Guardado localmente — sync con Relbase pendiente')
      } else {
        notifications.showSuccess(successTitle)
      }
    },
    async save() {
      const product = {
        name: this.name,
        sku: this.sku,
        width: this.width,
        height: this.height,
        price: this.price,
        status: this.status,
        logistic: this.logistic,
        is_margin: this.is_margin,
        margin_price: this.margin_price,
        sell_unit_m2: this.sell_unit_m2,
        customer: this.customer ? this.customer.id : null,
        family: this.family,
        line: this.line,
        recipe: this.product_receta.map((r, index) => ({
          id: r.id,
          qty: r.qty || 1,
          sort: index
        })),
        supplies: this.supplies.map(s => ({
          id: s.id,
          qty: s.qty
        }))
      }

      if (this.id > 0) product.id = this.id
      this.isLoading = true

      try {
        let response
        if (this.id > 0) {
          response = await updateProduct(product)
          // Opción A: actualizar relbase_product_id desde el response (puede haber cambiado si
          // la sincronización creó o actualizó el producto en Relbase durante el save)
          if (response.sync_result && response.sync_result.relbase_product_id) {
            this.relbase_product_id = response.sync_result.relbase_product_id
          }
          this.hasUnsavedChanges = false
          this._showSyncNotification(response, 'Producto actualizado: ' + this.name)
        } else {
          response = await createProduct(product)
          this.relbase_product_id = response.relbase_product_id || null
          this.hasUnsavedChanges = false
          this._showSyncNotification(response, 'Producto creado: ' + this.name)
        }
        this.$router.push('/products')
      } catch (e) {
        notifications.showError('Error al guardar: ' + e)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped>
.modern-card {
  border: 1px solid rgba(0,0,0,0.08) !important;
  border-radius: 12px !important;
  background-color: #fff;
}
.highlight-card {
  border-color: rgba(var(--v-primary-base), 0.2) !important;
}
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.modern-table { background: transparent !important; }
.no-border-radius { border-radius: 0 !important; }
.recipe-row { cursor: move; }
.recipe-row:hover { background-color: #f8f9fa; }
.bg-primary-light { background-color: rgba(var(--v-primary-base), 0.05); }
.border-primary-light { border: 1px solid rgba(var(--v-primary-base), 0.1); }
.bg-grey-light { background-color: #f8f9fa; }
.modern-input-small >>> .v-input__control { min-height: 32px !important; }
.modern-input-small >>> .v-input__slot { min-height: 32px !important; padding: 0 8px !important; }
.opacity-20 { opacity: 0.2; }
.shadow-sm { box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important; }
.lh-1 { line-height: 1.2; }
</style>